import { useRef, type CSSProperties, type ReactNode } from "react";
import { FastForward, Heart, Leaf, Pause, Rewind, Sun } from "lucide-react";
import { nw } from "@/components/landing/landing-data";
import { useEnteredView, useLandingReducedMotion, usePointerParallax } from "@/components/landing/landing-motion";

/**
 * The journal screenshot is assembled from tiles of the same image: each tile is a
 * rectangle (in the image's 1448 × 1086 pixels) showing one part of the page, and they
 * drop into place in `order`. The tiles cover the whole image edge to edge, so once
 * they've landed the result is the unchanged screenshot. Cuts run through empty canvas,
 * never through a sticker or card.
 */
const JOURNAL_W = 1448;
const JOURNAL_H = 1086;
const JOURNAL_TILES: { rect: [number, number, number, number]; order: number; tilt: number }[] = [
  { rect: [0, 0, 1448, 65], order: 0, tilt: 0 }, // "Your Unfoldings" header
  { rect: [250, 65, 740, 1000], order: 1, tilt: -0.6 }, // Life Lately page
  { rect: [740, 65, 1200, 1000], order: 2, tilt: 0.6 }, // Doing Hard Things page
  { rect: [0, 65, 250, 370], order: 3, tilt: -1.2 }, // thumbnails + polaroid
  { rect: [1200, 65, 1448, 240], order: 4, tilt: 1.2 }, // sticky notes
  { rect: [0, 370, 250, 600], order: 5, tilt: 1 }, // reminder
  { rect: [1200, 240, 1448, 390], order: 6, tilt: -1 }, // Core Insight
  { rect: [0, 600, 250, 725], order: 7, tilt: -0.8 }, // playlist
  { rect: [1200, 390, 1448, 550], order: 8, tilt: 0.8 }, // What Surfaced
  { rect: [0, 725, 250, 1000], order: 9, tilt: 1.2 }, // right now
  { rect: [1200, 550, 1448, 710], order: 10, tilt: -1 }, // Sit With This
  { rect: [1200, 710, 1448, 1000], order: 11, tilt: 1 }, // My Next Steps
  { rect: [0, 1000, 1448, 1086], order: 12, tilt: 0 }, // zoom, toolbar, button
];
/** The floating pieces around the window land after the page has assembled. */
const PIECES_AFTER_MS = 1500;

const pct = (v: number, of: number) => `${((v / of) * 100).toFixed(3)}%`;
const clipFor = ([x0, y0, x1, y1]: [number, number, number, number]) =>
  `inset(${pct(y0, JOURNAL_H)} ${pct(JOURNAL_W - x1, JOURNAL_W)} ${pct(JOURNAL_H - y1, JOURNAL_H)} ${pct(x0, JOURNAL_W)})`;

/** `--u` units: the pixel sizes from the V6 reference, scaled by .lp-piece (see landing.css). */
const u = (n: number) => `calc(var(--u) * ${n})`;

/**
 * One collage piece. Three layers so the motions never fight over `transform`:
 * the outer box is placed/rotated and follows the pointer (--d), the middle one
 * assembles on entry (from --fx/--fy/--fr), the inner one lifts on hover.
 */
function Piece({
  className,
  width,
  depth,
  from,
  delay,
  children,
}: {
  className: string;
  width: number;
  depth: number;
  from: [string, string, string];
  delay: number;
  children: ReactNode;
}) {
  return (
    <div className={`lp-piece lp-layer z-[3] ${className}`} style={{ width: u(width), "--d": depth } as CSSProperties}>
      <div
        className="lp-asm"
        style={{ "--fx": from[0], "--fy": from[1], "--fr": from[2], "--delay": `${PIECES_AFTER_MS + delay}ms` } as CSSProperties}
      >
        <div className="lp-lift relative">{children}</div>
      </div>
    </div>
  );
}

function CoreInsightPiece() {
  return (
    <Piece className="rotate-[4deg] lg:right-[-6.9%] lg:top-[16%]" width={240} depth={20} from={["-130px", "70px", "11deg"]} delay={120}>
      <span className="lp-tape lp-tape-sky -rotate-[4deg]" />
      <div className="lp-piece-card" style={{ padding: `${u(18)} ${u(20)} ${u(16)}` }}>
        <p className="flex items-center font-semibold" style={{ gap: u(10), fontSize: u(17) }}>
          <Sun className="text-[#D9822B]" style={{ width: u(20), height: u(20) }} strokeWidth={1.8} />
          Core Insight
        </p>
        <p className="flex items-start" style={{ gap: u(10), marginTop: u(12), fontSize: u(15), lineHeight: 1.4 }}>
          <Leaf className="shrink-0 fill-[#A9BE9C] text-[#8FA682]" style={{ width: u(20), height: u(20) }} />
          Let go of what doesn’t serve me.
        </p>
        <Heart className="ml-auto" style={{ width: u(16), height: u(16), marginTop: u(6) }} strokeWidth={1.8} />
      </div>
    </Piece>
  );
}

function StickyNotePiece() {
  return (
    <Piece className="rotate-[6deg] lg:left-[67%] lg:top-[91.5%]" width={150} depth={-12} from={["-60px", "80px", "15deg"]} delay={240}>
      <span className="lp-tape lp-tape-peach -rotate-[6deg]" />
      <div className="lp-sticky" style={{ padding: `${u(22)} ${u(16)} ${u(16)}`, fontSize: u(19) }}>
        same girl
        <br />
        new perspective
        <Heart className="mt-1" style={{ width: u(18), height: u(18) }} strokeWidth={1.8} />
      </div>
    </Piece>
  );
}

function PlaylistPiece() {
  return (
    <Piece className="rotate-[3deg] lg:left-[-7.6%] lg:top-[52%]" width={250} depth={-18} from={["120px", "-60px", "9deg"]} delay={0}>
      <div className="lp-piece-card" style={{ padding: `${u(14)} ${u(16)} ${u(12)}` }}>
        <div className="flex items-center" style={{ gap: u(12) }}>
          <span
            className="shrink-0"
            style={{
              width: u(44),
              height: u(44),
              borderRadius: u(8),
              background: "linear-gradient(180deg, #3d4f7c 0%, #c9876a 55%, #f1c58a 72%, #2a3552 100%)",
            }}
          />
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block font-semibold" style={{ fontSize: u(15) }}>
              Brighter Days
            </span>
            <span className="block text-[#6f6760]" style={{ fontSize: u(12), marginTop: u(3) }}>
              A Feel Good Playlist
            </span>
          </span>
          <Heart style={{ width: u(18), height: u(18) }} strokeWidth={1.8} />
        </div>
        <div className="rounded-full bg-[#e3dfd9]" style={{ height: u(3), marginTop: u(14) }}>
          <div className="h-full w-[20%] rounded-full bg-[#3a3531]" />
        </div>
        <div className="flex justify-between text-[#6f6760]" style={{ fontSize: u(11), marginTop: u(4) }}>
          <span>0:12</span>
          <span>-3:24</span>
        </div>
        <div className="flex items-center justify-center" style={{ gap: u(26), marginTop: u(4) }}>
          <Rewind className="fill-current" style={{ width: u(20), height: u(20) }} />
          <Pause className="fill-current" style={{ width: u(24), height: u(24) }} />
          <FastForward className="fill-current" style={{ width: u(20), height: u(20) }} />
        </div>
      </div>
    </Piece>
  );
}

/**
 * Marketing view of a journal page. The real Journal product is untouched.
 * Motion: when the window comes into view the journal page assembles itself: its parts
 * drop in from above one after another and settle into place. Then the floating pieces
 * around the window settle in, and drift slightly with the pointer.
 */
export function JournalSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const collageRef = useRef<HTMLDivElement>(null);
  const reduce = useLandingReducedMotion();
  const entered = useEnteredView(collageRef, 0.25);
  usePointerParallax(sectionRef);

  return (
    <section
      ref={sectionRef}
      id="journal"
      aria-labelledby="lp-journal-title"
      className="relative overflow-hidden pb-16 pt-20 md:pb-[60px] md:pt-[130px]"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div
          className="lp-wash left-[2%] top-[360px] h-[520px] w-[620px]"
          style={{ background: "radial-gradient(closest-side, rgb(226 239 228 / 0.6), rgb(226 239 228 / 0))" }}
        />
        <div
          className="lp-wash right-[2%] top-[260px] h-[520px] w-[620px]"
          style={{ background: "radial-gradient(closest-side, rgb(225 236 248 / 0.6), rgb(225 236 248 / 0))" }}
        />
      </div>

      <div className="relative mx-auto flex max-w-[1200px] flex-col items-center gap-[18px] px-4 text-center sm:px-8 lg:px-12">
        <p className="lp-eyebrow">Your Unfolding</p>
        <h2 id="lp-journal-title" className="lp-h2">
          Not a summary. <span className="lp-accent">A digital&nbsp;journal.</span>
        </h2>
        <p className="lp-lede max-w-[560px]">{nw("Your thoughts, visual memories and insights — stitched into one page.")}</p>
      </div>

      <div className="relative mx-auto mt-12 max-w-[1200px] px-4 sm:px-8 md:mt-[70px] lg:px-12">
        <div ref={collageRef} data-in={entered || reduce} className="lp-collage relative mx-auto w-full lg:mb-[9%] lg:w-[83.3%]">
          <div className="lp-jwin lp-layer relative z-[2]" style={{ "--d": 4 } as CSSProperties}>
            <div
              role="img"
              aria-label="An UnfoldWin journal: two collage entries, Life Lately and Doing Hard Things, surrounded by notes, playlists, insights and next steps"
              className="lp-jcanvas relative"
              style={{ aspectRatio: `${JOURNAL_W} / ${JOURNAL_H}` }}
            >
              {JOURNAL_TILES.map((t) => (
                <div
                  key={t.order}
                  className="lp-jtile absolute inset-0"
                  style={{ clipPath: clipFor(t.rect), "--i": t.order, "--r": `${t.tilt}deg` } as CSSProperties}
                >
                  <img
                    src="/images/landing/v6/journal-collage.webp"
                    srcSet="/images/landing/v6/journal-collage-820.webp 820w, /images/landing/v6/journal-collage.webp 1448w"
                    sizes="(min-width: 1200px) 920px, (min-width: 768px) 80vw, 100vw"
                    alt=""
                    width={JOURNAL_W}
                    height={JOURNAL_H}
                    loading="lazy"
                    decoding="async"
                    className="block h-full w-full"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Phones and tablets: the pieces sit in a row under the window. From lg up they overlap its edges. */}
          <div aria-hidden className="relative z-[3] -mt-6 flex flex-wrap items-start justify-center gap-4 sm:gap-6 lg:contents">
            <CoreInsightPiece />
            <StickyNotePiece />
            <PlaylistPiece />
          </div>
        </div>
      </div>
    </section>
  );
}
